import { getJwtToken } from "@/helpers/storage.helpers";
import HttpError from "./HttpError";

// Based on https://gist.github.com/johnhunter/ccf5cf63d146975e894cbc8bb71cecc9

// Narrow the headers type to only allow an object
type CustomRequestInit = Omit<RequestInit, "headers"> & {
  /**
   * Headers to append to request.
   * Authorization header is automatically applied as per arenko-ui.
   */
  headers?: Record<string, string>;
  /**
   * ThingsBoard uses X-Authorization header for no apparent reason.
   * @see https://github.com/thingsboard/thingsboard/issues/3458
   */
  useXAuthHeader?: boolean;
  /** Do not return or JSON parse the response body */
  ignoreResponse?: boolean;
};

const nonJSONResponseCodes = [502, 500, 401];

/**
 * Augments native fetch with error handling and typed response data.
 * Defaults to GET method and application/json content-type
 */
const fetch = async <RData = unknown>(
  input: string | URL | Request,
  init: CustomRequestInit = {}
): Promise<RData> => {
  const { ignoreResponse, useXAuthHeader, headers, ...restInit } = init;

  const authHeaderName = useXAuthHeader ? "X-Authorization" : "Authorization";

  const initWithDefaults = {
    method: "GET",
    ...restInit,

    headers: {
      [authHeaderName]: `Bearer ${getJwtToken()}`,
      "Content-Type": "application/json",
      ...headers,
    },
  };

  let res: Response;
  let resJson = undefined as RData;

  try {
    res = await window.fetch(input, initWithDefaults);
  } catch (err: unknown) {
    // catch general fetch errors
    throw new HttpError(undefined, { cause: err as Error });
  }

  if (!ignoreResponse && !nonJSONResponseCodes.includes(res.status)) {
    // conditionally catch JSON parse errors
    try {
      resJson = (await res.json()) as RData;
    } catch (err: unknown) {
      throw new HttpError(res, { cause: err as Error });
    }
  }

  if (res.ok) {
    return resJson;
  } else {
    throw new HttpError(res);
  }
};

export default fetch;
